import { useState } from "react";
import { WEEKDAYS, emptySchedule, formatMinutes, parseDurationToMinutes } from "../../utils/timesheets";

const inputClass =
  "w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

/**
 * Weekrooster voor een interne lijn: basisuren per weekdag, voor 1 of 2 weken.
 * `value` is het schedule-object uit de API ({cycle_weeks, anchor_date, weeks:[{mon..sun: minuten}]}).
 * Teksten per dag worden lokaal bijgehouden zodat een half ingetikte waarde niet verspringt;
 * pas bij het verlaten van het veld wordt ze naar minuten omgezet.
 */
export default function ScheduleEditor({ value, onChange }) {
  const schedule = value || emptySchedule(1);
  const [drafts, setDrafts] = useState({});

  const setCycle = (cycleWeeks) => {
    const next = emptySchedule(cycleWeeks, schedule.anchor_date || "");
    next.weeks = next.weeks.map((week, index) => schedule.weeks[index] || week);
    setDrafts({});
    onChange(next);
  };

  const commit = (weekIndex, dayKey, text) => {
    const minutes = parseDurationToMinutes(text);
    const draftKey = `${weekIndex}-${dayKey}`;
    if (minutes === null) {
      setDrafts((d) => ({ ...d, [draftKey]: { text, invalid: true } }));
      return;
    }
    setDrafts((d) => {
      const { [draftKey]: _removed, ...rest } = d;
      return rest;
    });
    const weeks = schedule.weeks.map((week, i) => (i === weekIndex ? { ...week, [dayKey]: minutes } : week));
    onChange({ ...schedule, weeks });
  };

  const weekTotal = (week) => WEEKDAYS.reduce((sum, d) => sum + (week[d.key] || 0), 0);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Rooster</label>
          <select
            value={schedule.cycle_weeks}
            onChange={(e) => setCycle(Number(e.target.value))}
            className={inputClass}
          >
            <option value={1}>Vast wekelijks rooster</option>
            <option value={2}>Tweewekelijks (week 1 / week 2)</option>
          </select>
        </div>
        {schedule.cycle_weeks === 2 && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Maandag van week 1</label>
            <input
              type="date"
              value={schedule.anchor_date || ""}
              onChange={(e) => onChange({ ...schedule, anchor_date: e.target.value })}
              className={inputClass}
            />
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="text-sm">
          <thead>
            <tr className="text-xs text-gray-500 uppercase">
              <th className="px-2 py-1 text-left"></th>
              {WEEKDAYS.map((d) => (
                <th key={d.key} className="px-2 py-1 text-center">{d.short}</th>
              ))}
              <th className="px-2 py-1 text-right">Totaal</th>
            </tr>
          </thead>
          <tbody>
            {schedule.weeks.map((week, weekIndex) => (
              <tr key={weekIndex}>
                <td className="px-2 py-1 text-gray-600 whitespace-nowrap">
                  {schedule.cycle_weeks === 2 ? `Week ${weekIndex + 1}` : "Basisuren"}
                </td>
                {WEEKDAYS.map((d) => {
                  const draft = drafts[`${weekIndex}-${d.key}`];
                  return (
                    <td key={d.key} className="px-1 py-1">
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="–"
                        value={draft ? draft.text : formatMinutes(week[d.key])}
                        onChange={(e) =>
                          setDrafts((cur) => ({ ...cur, [`${weekIndex}-${d.key}`]: { text: e.target.value } }))
                        }
                        onBlur={(e) => commit(weekIndex, d.key, e.target.value)}
                        className={`w-16 text-center border rounded px-1 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          draft?.invalid ? "border-red-400 bg-red-50" : "border-gray-300"
                        }`}
                        title="Bv. 9:00, 7:30 of 7,5"
                      />
                    </td>
                  );
                })}
                <td className="px-2 py-1 text-right tabular-nums text-gray-700">
                  {formatMinutes(weekTotal(week)) || "0:00"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
